import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { ActionBar } from './ActionBar.jsx'
import { ActStrip, ConfirmStaleNote, useConfirmAfterApplied } from './ActStrip.jsx'
import AddPanel from './AddPanel.jsx'
import AvatarCircle from './AvatarCircle.jsx'
import Disclosure from './Disclosure.jsx'
import Person, { MemberLink } from './Person.jsx'
import WatchScreenTabs from './WatchScreenTabs.jsx'
import { freshness, instantOf } from './freshness.js'
import {
  DISCORD_HOME,
  LOADING,
  MISCONFIGURED,
  READY,
  STANDING_CAUGHT_UP,
  awaitingBaseText,
  catchUpTarget,
  countdownFor,
  countdownForInstant,
  defaultSeasonNumber,
  deriveYou,
  deriveYourProgress,
  easternClock,
  episodeCode,
  groupLeaderIds,
  hueForTitle,
  imageUrl,
  isAwaitingNextSeason,
  isExactCountdown,
  isFinalEpisode,
  isStale,
  isTonight,
  kindLabel,
  longEasternDate,
  memberProfilePath,
  membersWithStatus,
  movieRuntimeText,
  nextEpisodeToOffer,
  providersOld,
  seasonEpisodeCountsOf,
  seasonTrackFor,
  serverStanding,
  seriesRowsByListTab,
  shortEasternDate,
  showStanding,
  sourceLabel,
  subscriptionsByListTab,
  subscriptionsByStatus,
  subscriptionsOf,
  useWatch,
  weekBuckets,
  weekIsEmpty,
  weekdayEasternDate,
  yourStanding,
} from './watch.js'
import {
  KIND_SET_PROGRESS,
  KIND_SET_STATUS,
  TARGET_STOPPED,
  TARGET_WATCHED,
  TARGET_WATCHING,
  UNREACHABLE,
  statusCommand,
  useWatchRequest,
} from './watchRequests.js'
import { seasonLabel } from './watchTitle.js'
import { UNKNOWN_NAME, nameFor } from '../directory.js'

// V2-8a: `ActStrip`/`ConfirmStaleNote`/`useConfirmAfterApplied` are
// re-exported below unchanged, so `WatchTitle.jsx` (and anything else that
// already imports them from this file) needs no import-path change — see
// `ActStrip.jsx`'s own module header for why `useConfirmAfterApplied` and
// `ConfirmStaleNote` moved there rather than staying here.
export { ConfirmStaleNote, useConfirmAfterApplied }

// The watch panel — the week guide, the title card in every state, the
// finished archive, and dropped titles. W2b (P6-244).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SPECIFICATION IS `docs/design-briefs/watch/HANDOFF.md`, NOT THIS FILE
// ═══════════════════════════════════════════════════════════════════════════
//
// Every rendering decision below traces to a numbered state in that document's
// "Every state" table or to its own numbered findings — see the brief
// (`docs/briefs/W2b-watch-panel.md`) and `docs/reports/W2b.md` for the map
// from state number to test name. Do not redesign anything the handoff
// settles; a state that could not be built as drawn is a finding in the
// report, not a silent substitution.
//
// ═══════════════════════════════════════════════════════════════════════════
// ✅ P6-268 IS NO LONGER PENDING — THIS PARAGRAPH USED TO SAY IT WAS
// ═══════════════════════════════════════════════════════════════════════════
//
// It read: "a status button needs its own ADR (P6-268, pending). ... There
// is no `<form>`, no `<input>`, no `<button>` that mutates anything the
// store holds." ADR-0009 is now accepted, and `MemberActionRow`/
// `ProgressStepper` below are exactly that button, built the way the ADR
// requires: they never write to `watch_data.json` directly (ADR-0005 D2
// still stands unchanged) — they leave a REQUEST with the platform service
// (`src/portal/watchRequests.js`), which the bot's own poll applies through
// the same manager methods `/watch watching` and `/watch finish` already
// call (`cogs/watch/requests.py`). Everything else this paragraph said is
// still true: nothing here computes progress or status locally, and every
// OTHER interactive element is still a disclosure or a link that leaves
// this page.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 FINDING — THE ONE EMBER LINK THE HANDOFF WANTS CANNOT BE BUILT AS DRAWN
// ═══════════════════════════════════════════════════════════════════════════
//
// Handoff's interaction table: "Add a title in Discord ↗ | Deep-link to the
// channel." `3a/watch.json` publishes no `guild_id` and no `channel_id`
// anywhere a client can read (the only place either appears is already baked
// into a formed thread URL, per-episode, and only once a thread exists) — see
// `docs/reports/W2b.md` for the full finding. It is not only a missing field:
// WT2's own ruling is "posts go to the channel the subscription was created
// in, no announcement-channel setting," so there is no single "#watch
// channel" for a sitewide link to name even in principle — a title added from
// one channel and a title added from another have two different "the
// channel"s. `DISCORD_HOME` below opens Discord itself (a real, always-valid
// URL) rather than a fabricated guild link; the member is left to navigate
// from there. The copy says "Open Discord," not "Add a title," so it does not
// promise a destination it cannot reach.
//
// ⚠️ MOVED TO `watch.js` FOR W3b (P6-244) — the title page's own "Open #watch
// ↗" carries the identical finding (spec §5, the "we're here" control), so
// the constant now lives beside `DEPLOYMENT_FAULT_STATUS` and the other
// cross-component constants rather than being re-typed a second time.

/** 2:3 poster, or the dashed absence — `MoviePanel.jsx::PosterSlot`'s exact
 * pattern, applied to a field that is already a full URL (never a TMDb
 * relative path) from either of watch's two providers.
 *
 * Exported: W3b's title-page hero reuses this at a third size (`hero`, 132px
 * — `.d2d-watch__poster--hero` in app.css) rather than re-typing the broken-
 * image handling a second time. */
export function PosterSlot({ subscription, size = 'card' }) {
  const [broken, setBroken] = useState(false)
  const src = broken ? null : imageUrl(subscription?.poster_url)
  return (
    <span
      className={`d2d-watch__poster d2d-watch__poster--${size}`}
      data-poster={src ? 'true' : 'false'}
    >
      {src ? (
        <img
          className="d2d-watch__posterimg"
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
        />
      ) : (
        <span className="d2d-micro">no poster</span>
      )}
    </span>
  )
}

/** One provider logo, 22px, or nothing at all — Handoff: "the name is the
 * information and the logo is decoration," so a broken logo drops silently
 * rather than drawing a placeholder box inside an already-labelled chip.
 *
 * Exported for the same reason `PosterSlot` is: the title page's hero draws
 * an identical provider row. */
export function ProviderLogo({ url }) {
  const [broken, setBroken] = useState(false)
  const src = broken ? null : imageUrl(url)
  if (!src) return null
  return (
    <img
      className="d2d-watch__providerlogo"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
    />
  )
}

/**
 * The standing strip — three chips, always present, always labelled. Handoff:
 * "the reason no surface here can say a bare *finished*." `you` is `null`
 * when signed out, per the brief's rule 2 — the strip then draws only two
 * chips, never a placeholder third.
 *
 * Exported: the title-page hero carries the same strip (Handoff §5: "132px
 * poster, standing strip, H1...").
 */
export function StandingStrip({ subscription, directory, you }) {
  const show = showStanding(subscription)
  const server = serverStanding(subscription)
  return (
    <div className="d2d-watch__strip">
      <span className="d2d-watch__chip d2d-micro">
        <span className="d2d-watch__chipprefix">SHOW</span> {show.label}
      </span>
      <span className="d2d-watch__chip d2d-micro">
        <span className="d2d-watch__chipprefix">SERVER</span> {server.label}
      </span>
      {you.signedIn ? (
        you.status ? (
          <span className="d2d-watch__chip d2d-watch__chip--you d2d-micro" data-testid="you-chip">
            <span className="d2d-watch__chipprefix">YOU</span>{' '}
            {
              // P6-282: a THIRD value now reaches this chip (`stopped`) --
              // the old binary `? 'FINISHED' : 'WATCHING'` would have read
              // a stopped member as "WATCHING", exactly the false claim
              // this store's own STATUS_* / MEMBER_STATUS_* split exists
              // to prevent (P6-261). Named explicitly for all three so an
              // unrecognised future value falls back honestly rather than
              // silently reading as "WATCHING" again.
              //
              // P6-299b: a FOURTH reading, `you.standing === STANDING_CAUGHT_UP`
              // (`deriveYou`, `watch.js`) -- checked LAST, after FINISHED and
              // STOPPED, matching `_member_standing`'s own precedence
              // (bot-side): `caught_up` only ever applies to a member whose
              // raw `status` is still `watching` in the first place, so this
              // ordering can never mask FINISHED or STOPPED even if a stale
              // `standing` value somehow disagreed. Read verbatim off the
              // published field, never recomputed here (brief §2.2: "Never
              // compute it on the site").
              you.status === TARGET_WATCHED
                ? 'FINISHED'
                : you.status === TARGET_STOPPED
                  ? 'STOPPED'
                  : you.standing === STANDING_CAUGHT_UP
                    ? 'CAUGHT UP'
                    : 'WATCHING'
            }
          </span>
        ) : (
          <span
            className="d2d-watch__chip d2d-watch__chip--dashed d2d-micro"
            data-testid="you-chip-dashed"
          >
            <span className="d2d-watch__chipprefix">YOU</span> NOT MARKED IN
          </span>
        )
      ) : null}
    </div>
  )
}

/** The honest replacement for a next-up block — Handoff view 1, item 4.
 *
 * Exported: the title page's hero has the identical no-next-up branch (a
 * film's release date, a between-seasons show, an ended show, a just-added
 * title) and Handoff draws no second version of these four sentences. */
export function WhereThingsStandBlock({ subscription }) {
  const status = subscription?.series_status
  if (status === null || status === undefined) {
    return (
      <div className="d2d-watch__stand" data-testid="stand-waiting">
        <div className="d2d-micro d2d-watch__label">WAITING FOR THE FIRST CHECK</div>
        <p className="d2d-watch__standline">
          The bot checks it on its next nightly pass. Nothing is wrong.
        </p>
      </div>
    )
  }
  if (subscription?.kind === 'movie' && subscription?.release_date) {
    const releasedMs = instantOf(subscription.release_date)
    return (
      <div className="d2d-watch__stand" data-testid="stand-released">
        <div className="d2d-micro d2d-watch__label">
          RELEASED {releasedMs === null ? '' : longEasternDate(releasedMs)}
        </div>
      </div>
    )
  }
  if (status === 'ended' || status === 'cancelled') {
    const code = episodeCode(subscription?.last_aired?.season ?? null, subscription?.last_aired?.episode)
    return (
      <div className="d2d-watch__stand" data-testid="stand-nothing-coming">
        <div className="d2d-micro d2d-watch__label">NOTHING COMING</div>
        <p className="d2d-watch__standline">
          {code
            ? `The story ends at ${code}. There is nothing to wait for.`
            : 'The story has ended. There is nothing to wait for.'}
        </p>
      </div>
    )
  }
  const news = Array.isArray(subscription?.news) ? subscription.news : []
  const newsLine = news.find((entry) => typeof entry?.detail === 'string')?.detail ?? null
  return (
    <div className="d2d-watch__stand" data-testid="stand-no-date">
      <div className="d2d-micro d2d-watch__label">NO DATE YET</div>
      <p className="d2d-watch__standline">{newsLine ?? 'No new episode date yet.'}</p>
    </div>
  )
}

// `useConfirmAfterApplied` and `ConfirmStaleNote` moved to `ActStrip.jsx` in
// V2-8a (re-exported below, unchanged in behaviour) — `ActStrip.jsx` needs
// `ConfirmStaleNote` for its own Done state, and this file needs `ActStrip`
// itself for `ProgressStepper`/`MemberActionRow` below; importing one
// direction only (this file importing FROM `ActStrip.jsx`) avoids the
// import cycle that defining both here would create. Every existing
// consumer (`WatchTitle.jsx`) still imports both names from THIS file —
// see the re-export at the bottom.

/**
 * The progress control — P6-281. `watch.set_progress`, set-shaped
 * (nobody else's position can collide with yours), offering ONE of two
 * things, never both:
 *
 * - **"+1"**, from YOUR OWN position (`yourProgress` —
 *   `deriveYourProgress`, `watch.js`) to the next episode in the same
 *   season, when `nextEpisodeToOffer` says there is a real one to offer.
 * - **The one-tap catch-up**, "I've seen up to E<latest aired>"
 *   (`catchUpTarget`, `watch.js`) — brief §1.3: offered only when you
 *   have NO position of your own yet at all, and only once
 *   (`nextEpisodeToOffer` already requires a real `yourProgress` to
 *   return anything, so the two never both apply to the same press).
 *
 * 🔴 THAT GATE REPLACED A PLAIN `toWatchInfo(...).mode === 'behind'`
 * CHECK (manager review, 2026-09-23, kept true under P6-281): `behind`
 * happens to imply the same bound today, but it is a DISPLAY helper's
 * own definition (and, since P6-281, one built from the GROUP's
 * position, not yours), not a safety property anyone designed or tested
 * as one. The button's `to` comes directly from the gate's own returned
 * target, never from raw arithmetic, so an ungated position can never
 * reach the request payload.
 *
 * `unavailable`, when given, REPLACES this component's own default
 * "buttons are unavailable" note with a caller-supplied fallback — used
 * by `WatchTitle.jsx`'s `ActionBar` usage to fall back to the read-only
 * copy-the-command control the moment the platform service itself
 * reports unreachable, so the two are never both on screen. See that
 * caller for the other half: when there is nothing to offer at all
 * (neither a "+1" nor a catch-up), it does not mount this component in
 * the first place, for the identical reason.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * 🔴 V2-8a — "· FINALE", THE SITE'S HALF OF A CONTRACT WITH THE BOT
 * ═══════════════════════════════════════════════════════════════════════
 *
 * The button's own label (and the honest "you're on SxEy" text `ActStrip`
 * shows once applied) name the target episode as the FINALE when
 * `isFinalEpisode` (`watch.js`) — the site's own implementation of the
 * bot's `_is_final_episode`, checked against it through one shared
 * fixture — recognises it as one. `ember`, new: the title page passes
 * `true` so this is drawn as the design's one ember-filled primary
 * action; a caller that omits it (the card, until V2-8b decides
 * otherwise) keeps the plain bordered button this control has always
 * drawn.
 */
export function ProgressStepper({ subscription, yourProgress, userId, onChanged, unavailable, ember = false }) {
  const request = useWatchRequest()
  const target = nextEpisodeToOffer(subscription, yourProgress)
  const catchUp = target ? null : catchUpTarget(subscription, yourProgress)
  const effectiveTarget = target ?? catchUp

  const isConfirmed = (shared) => {
    const fresh = subscriptionsOf(shared).find((row) => row.id === subscription?.id)
    const mine = deriveYourProgress(fresh, userId)
    return (
      mine?.season === effectiveTarget?.season && mine?.episode === effectiveTarget?.episode
    )
  }
  const confirmFailed = useConfirmAfterApplied(request, onChanged, isConfirmed)

  if (request.status === UNREACHABLE && unavailable) return unavailable()

  const press = () =>
    effectiveTarget &&
    request.ask({
      kind: KIND_SET_PROGRESS,
      target: subscription.id,
      payload: { to: effectiveTarget },
    })

  if (request.status) {
    const isFinale = effectiveTarget ? isFinalEpisode(subscription, effectiveTarget.season, effectiveTarget.episode) : false
    const code = effectiveTarget ? episodeCode(effectiveTarget.season, effectiveTarget.episode) : null
    // V2-4c: the finale branch adds the design's own pointer, verbatim
    // ("Finished ✓ — that was the final episode. Rate it if you like.",
    // `D2D Watch v2.dc.html` line 1148) — APPENDED to, never replacing,
    // V2-8a's own "You're on SxEy." lead-in: `watch-title.test.jsx`'s
    // existing `toHaveTextContent('That was the final episode.')` assertion
    // is a substring check (gotcha 1's own "a substring proves presence,
    // never absence," brief §3), so this stays green while adding the new
    // sentence rather than restructuring an already mutation-tested string.
    const doneText = code
      ? `You're on ${code}.${isFinale ? ' That was the final episode. Rate it if you like.' : ''}`
      : null
    return (
      <ActStrip
        request={request}
        confirmFailed={confirmFailed}
        testId="progress-stepper-status"
        confirmStaleTestId="progress-stepper-confirm-stale"
        doneText={doneText}
        onRetry={effectiveTarget ? press : undefined}
      />
    )
  }

  if (!effectiveTarget) return null

  const isFinale = isFinalEpisode(subscription, effectiveTarget.season, effectiveTarget.episode)
  const label = target
    ? `+1 · ${episodeCode(target.season, target.episode)}`
    : `I've seen up to ${episodeCode(catchUp.season, catchUp.episode)}`

  return (
    <button
      type="button"
      className={`d2d-watch__stepperbtn${ember ? ' d2d-watch__stepperbtn--ember' : ''}`}
      data-testid="progress-stepper"
      disabled={request.busy}
      onClick={press}
    >
      {label}
      {isFinale ? ' · finale' : ''}
    </button>
  )
}

/**
 * Your status on this show — V2-3b, ADR-0009, reworded by P6-281 §1.2, then
 * reworded AGAIN by P6-282. `watch.set_status`, the same function `/watch
 * watching` and `/watch stopped` already have.
 *
 * 🔴 P6-282, Jordan verbatim: *"our 'I've finished it' button needs to be
 * something more like 'I am no longer watching this'."* Shown that one
 * button covering both "finished" and "quit" would leave the game unable
 * to count completions, he ruled **"Watching / Stopped; finish via
 * episodes"** — this is the THIRD wording this control has carried
 * ("I'm in" / "I've watched it" → "I'm watching" / "I've finished it" →
 * this), and the pattern across all three is the same one gotcha 18
 * names for prose: an unwanted framing (here, "the site status control is
 * how you finish something") survives because nothing in it was
 * technically wrong, only not what was actually asked for.
 *
 * **A series** now offers exactly two buttons — **"I'm watching"** /
 * **"I've stopped watching"** — never a "mark finished" button at all.
 * Finishing a series comes ONLY from marking its own final episode
 * (`set_member_progress`'s auto-finish, `utils/watch_manager.py`) or an
 * explicit Discord override (`/watch watched`, `/watch log`) — neither
 * reachable from this control, by design. Once `you.status` reads
 * `watched`, this renders a plain **"Finished ✓"** state instead of the
 * button pair, with a pointer to Discord for the one thing this slice
 * does not build: *"the rating control is a later slice"* (brief §2) —
 * never worded to imply the site itself saved anything.
 *
 * **A movie** has no episodes to mark final at all (brief, verbatim), so
 * it keeps the ORIGINAL single-button shape this control has always had
 * for a completion mark — relabelled **"I watched it"**, the plainer,
 * movie-flavoured phrase `/watch watched`'s own command description
 * already uses.
 *
 * 🔴 ADDITIVE, NOT A REPLACEMENT (brief §3, unchanged since P6-281).
 * V2-8b's row no longer draws a "who is in" text block at all (the avatar
 * stack replaces it — see `RowFaceStack`), but this control's own behaviour
 * is unchanged: it renders as its own row, never folded into anything else.
 * Renders nothing at all when
 * signed out (spec §7: "Signed-out rows render the chips and no
 * controls") — the caller never even reaches `useWatchRequest`'s `ask` in
 * that case.
 *
 * `userId` — P6-281's own addition, needed to build the confirm-after-
 * applied predicate (`useConfirmAfterApplied`, above): after a press
 * reads APPLIED, this re-derives YOUR status from a fresh read rather
 * than trusting the `you` prop, which is still the pre-press snapshot
 * until the parent re-renders with new data.
 */
export function MemberActionRow({ subscription, you, userId, onChanged }) {
  const request = useWatchRequest()
  const lastTargetRef = useRef(null)

  const isConfirmed = (shared) => {
    const fresh = subscriptionsOf(shared).find((row) => row.id === subscription?.id)
    return deriveYou(fresh, userId).status === lastTargetRef.current
  }
  const confirmFailed = useConfirmAfterApplied(request, onChanged, isConfirmed)

  if (!you.signedIn) return null

  const press = (target) => {
    lastTargetRef.current = target
    return request.ask({
      kind: KIND_SET_STATUS,
      target: subscription.id,
      payload: { status: target },
    })
  }

  // V2-8a: "the new value in words" (ActStrip's Done state) and "the
  // equivalent Discord command in mono" (its Unavailable state) are both
  // built from `lastTargetRef.current` — exactly what THIS press asked
  // for, never guessed and never read off the wire (`docs/reports/
  // V2-3b.md` §1: the platform service's own `applied` outcome for a
  // set-shaped method carries no member-facing prose at all). `Retry`
  // re-sends the identical target as a genuinely NEW request (`press`
  // mints a fresh `client_key` inside `ask()`), per the brief's own words
  // for Expired.
  if (request.status) {
    const target = lastTargetRef.current
    const doneText =
      target === TARGET_WATCHED
        ? // V2-4c: the design's own wording, verbatim (`ActionBar.dc.html`'s
          // `statusSpec`/`filmSpec` doneText) — now that `watch.set_rating`/
          // `watch.set_review` exist, the movie completion pointer names the
          // control the site can actually offer, one row down.
          'Watched ✓ Rate it if you like.'
        : target === TARGET_STOPPED
          ? "You've stopped watching."
          : target === TARGET_WATCHING
            ? "You're watching."
            : null
    return (
      <div className="d2d-watch__actionrow" data-testid="member-action-row" data-state={request.status}>
        <ActStrip
          request={request}
          confirmFailed={confirmFailed}
          testId="member-action-status"
          confirmStaleTestId="member-action-confirm-stale"
          doneText={doneText}
          command={statusCommand(subscription, target)}
          onRetry={target ? () => press(target) : undefined}
        />
      </div>
    )
  }

  const isMovie = subscription?.kind === 'movie'

  // Series, already finished — brief §2: "a clear 'Finished ✓' state once
  // a member has marked the final episode." No buttons at all here; this
  // control does not offer a way back (moving off the final episode is
  // `/watch progress`'s own job — `utils/watch_manager.py`'s "move back"
  // rule — not something re-pressing a site button does).
  if (!isMovie && you.status === TARGET_WATCHED) {
    return (
      <div className="d2d-watch__actionrow" data-testid="member-action-row" data-state="finished">
        <span className="d2d-micro d2d-watch__label">YOUR STATUS</span>
        <div className="d2d-watch__actionbtns">
          <span className="d2d-watch__finishedbadge" data-testid="action-finished">
            Finished ✓
          </span>
        </div>
        <p className="d2d-micro d2d-watch__note" data-testid="finished-rate-pointer">
          Rate it in Discord with <code>/watch rate</code>.
        </p>
      </div>
    )
  }

  if (isMovie) {
    return (
      <div
        className="d2d-watch__actionrow"
        data-testid="member-action-row"
        role="group"
        aria-label="Your status on this title"
      >
        <span className="d2d-micro d2d-watch__label">YOUR STATUS</span>
        <div className="d2d-watch__actionbtns">
          <button
            type="button"
            className="d2d-watch__actionbtn"
            data-testid="action-watched-it"
            disabled={request.busy}
            aria-pressed={you.status === TARGET_WATCHED}
            onClick={() => press(TARGET_WATCHED)}
          >
            {you.status === TARGET_WATCHED ? 'I watched it ✓' : 'I watched it'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="d2d-watch__actionrow" data-testid="member-action-row" role="group" aria-label="Your status on this title">
      <span className="d2d-micro d2d-watch__label">YOUR STATUS</span>
      <div className="d2d-watch__actionbtns">
        <button
          type="button"
          className="d2d-watch__actionbtn"
          data-testid="action-im-in"
          disabled={request.busy}
          aria-pressed={you.status === TARGET_WATCHING}
          onClick={() => press(TARGET_WATCHING)}
        >
          {you.status === TARGET_WATCHING ? "I'm watching ✓" : "I'm watching"}
        </button>
        <button
          type="button"
          className="d2d-watch__actionbtn"
          data-testid="action-stopped"
          disabled={request.busy}
          aria-pressed={you.status === TARGET_STOPPED}
          onClick={() => press(TARGET_STOPPED)}
        >
          {you.status === TARGET_STOPPED ? "I've stopped watching ✓" : "I've stopped watching"}
        </button>
      </div>
      {you.status === TARGET_STOPPED ? (
        // P6-282 round 2, item 3: the brief's own words -- "stopping
        // prompts a rating and a review, exactly as finishing does... a
        // plain pointer to it on the site" -- mirroring the FINISHED
        // branch's `finished-rate-pointer` above, one status over. Names
        // both commands (finishing's pointer names only `/watch rate`)
        // because the brief pairs stopping with a review, not a rating
        // alone.
        <p className="d2d-micro d2d-watch__note" data-testid="stopped-rate-pointer">
          Rate and review it in Discord with <code>/watch rate</code> and <code>/watch review</code>.
        </p>
      ) : null}
    </div>
  )
}

/** Handoff state 11's ember callout: `NOT CHECKED SINCE 17 SEP · 3 DAYS AGO`. */
// ═══════════════════════════════════════════════════════════════════════════
// V2-8b — THE REDESIGNED WATCHLIST ROW
//
// Replaces the v1 `Card` (hue-less, StandingStrip/NextUpBlock/WhereWeAreBlock/
// ProviderRow/WhoIsInBlock/StaleStrip — all deleted above, none of it drawn by
// the v2 design's own row) with `docs/design-briefs/watch/v2/HANDOFF.md`
// screen 1's own six elements, per the brief's own enumerated list: the hue
// wash and a 72px poster, the title and meta (kind · year · source), the
// per-season episode track, an avatar stack of who is watching, the You box,
// and the ActionBar. `WatchTitle.jsx`'s own hero, standing box and progress
// lines are untouched — this is a SEPARATE, leaner presentation for the list,
// not a shrunk copy of the title page's.
// ═══════════════════════════════════════════════════════════════════════════

/** `kind · year · source` — brief §2: "Omit the year when it is null; never
 * print a placeholder." A plain array-join rather than a template string is
 * what makes the omission drop the surrounding separator too, instead of
 * leaving a double " · · " where the year would have been. */
function RowMeta({ subscription }) {
  const year = Number.isInteger(subscription?.year) ? subscription.year : null
  const parts = [kindLabel(subscription?.kind)]
  if (year !== null) parts.push(String(year))
  parts.push(sourceLabel(subscription?.kind))
  return <div className="d2d-watch__meta d2d-micro">{parts.join(' · ')}</div>
}

/** The per-season episode track — brief §2: "dusk for seen by you, grey for
 * the group ahead of you, outlined for aired but unseen, dashed for not yet
 * aired... use the same position arithmetic the title page's progress lines
 * use." `seasonEpisodeCountsOf`/`defaultSeasonNumber`/`seasonTrackFor` are
 * all `watch.js` — see that module's own V2-8b section for why each one is a
 * MOVE, never a second implementation. Renders nothing at all for a movie or
 * a title whose seasons have not been fetched yet (`seasons` is `[]`), the
 * identical honest-absence rule `SeasonsSection` (`WatchTitle.jsx`) already
 * draws for the same two cases. */
function SeasonTrack({ subscription, yourProgress }) {
  const seasons = seasonEpisodeCountsOf(subscription)
  const seasonNumber = defaultSeasonNumber(subscription, seasons)
  const season = seasons.find((s) => (s.number ?? null) === (seasonNumber ?? null)) ?? null
  const cells = season ? seasonTrackFor(subscription, season, yourProgress) : []
  if (cells.length === 0) return null

  return (
    <div className="d2d-watch__track" data-testid="row-track">
      <div className="d2d-micro d2d-watch__tracklabel">
        {seasonLabel(seasonNumber ?? null)} · {cells.length} episode{cells.length === 1 ? '' : 's'}
      </div>
      <div className="d2d-watch__trackcells" aria-hidden="true">
        {cells.map((cell, index) => {
          // Priority order matches `seasonTrackFor`'s own docstring exactly:
          // you, then group, then aired-but-unseen, then not-yet-aired — the
          // four are already mutually exclusive by construction there, so
          // this is a plain ternary rather than a second gate re-deciding it.
          const variant = cell.you ? 'you' : cell.group ? 'group' : cell.aired ? 'aired' : 'future'
          // eslint-disable-next-line react/no-array-index-key -- a season's
          // episode numbering is stable and contiguous; the index IS the
          // episode's own ordinal within this render.
          return <span key={index} className={`d2d-watch__trackcell d2d-watch__trackcell--${variant}`} />
        })}
      </div>
    </div>
  )
}

/** The avatar stack — brief §2: "a stack of overlapping 24 px avatars of the
 * people watching. Stopped members are not in it." Read literally against
 * this store's three statuses: `watching` AND `watched` (a mixed-progress
 * title has members in both at once — one member can finish while another is
 * still partway through), `stopped` excluded, the identical "not counted
 * among who is watching" rule the pre-v2 sections already used.
 *
 * V2-8b, manager review round 2: MOVED INSIDE `RowGroupBox` below (the
 * design's own layout — "the avatar stack and its note"), and given the
 * design's own cap: at most four shown, `+N more` beyond that, or `N on it`
 * when four or fewer (`D2D Watch v2.dc.html`'s `rich()`: `faces =
 * names.slice(0, 4)`). Ordering is UNCHANGED from round 1 (watching, then
 * watched, each in the store's own order) rather than the design's
 * position-sorted list — re-deriving a per-member sort was not asked for
 * here and the existing order is already honest, just not rank-ordered. */
function RowFaceStack({ subscription, directory, userId }) {
  const ids = [
    ...membersWithStatus(subscription, 'watching'),
    ...membersWithStatus(subscription, 'watched'),
  ]
  if (ids.length === 0) return null
  const shown = ids.slice(0, 4)
  const hidden = ids.length - shown.length
  return (
    <div className="d2d-watch__facestack" data-testid="row-faces">
      {
        // 🔴 MANAGER REVIEW ROUND 2 — REVERTED TO NON-INTERACTIVE. A first
        // draft made each face a `MemberLink`, but a 24px overlapping
        // circle in a capped four-wide stack cannot honestly meet this
        // system's own 44px floor ("tap targets are at least 44px
        // everywhere," not negotiable per screen) — and the design draws
        // this stack as decoration, never a control. A too-small link is
        // worse than none: it invites a mis-tap. See `RowGroupBox`'s own
        // `GroupLeaderNames` below for where a row-level entry point to a
        // member's profile now actually lives, and the report for the
        // full reasoning.
      }
      {shown.map((id, index) => {
        const person = nameFor(directory, id)
        const initial = person.known ? person.label.trim().charAt(0).toUpperCase() : '·'
        const isYou = id === userId
        return (
          <span
            key={id}
            className={`d2d-watch__face${isYou ? ' d2d-watch__face--you' : ''}`}
            title={person.known ? person.label : UNKNOWN_NAME}
            data-testid="row-face"
            // Cumulative overlap by index, never a negative margin — see the
            // CSS rule's own comment (`.d2d-watch__face`, app.css) for why.
            style={index === 0 ? undefined : { transform: `translateX(calc(var(--d2d-space-2) * -1 * ${index}))` }}
          >
            <AvatarCircle person={person} initial={initial} size={24} testId="row-face-avatar" />
          </span>
        )
      })}
      <span className="d2d-micro d2d-watch__facesnote" data-testid="row-faces-note">
        {hidden > 0 ? `+${hidden} more` : `${ids.length} on it`}
      </span>
    </div>
  )
}

/**
 * V2-2c, manager review round 2: the row's own entry point to a member's
 * profile, now that `RowFaceStack`'s avatars are decorative again (a 24px
 * overlapping circle cannot honestly meet the 44px floor). Mirrors
 * `Person.jsx::People`'s own "A, B and C" join EXACTLY — never a second
 * name-joining implementation, per that file's own header — but wraps each
 * name in `MemberLink` individually, which `People` itself does not do
 * (it is shared with movie/music, and gaining a link unconditionally is
 * not this lane's call to make for callers that never asked for one).
 *
 * `min-height: var(--d2d-hit-min)` lives on `.d2d-watch__leaderlink`
 * (app.css) via padding, not a bigger font — the manager's own words —
 * so a one-word name and a hyphenated one both clear the floor alike.
 */
function GroupLeaderNames({ directory, ids }) {
  const list = Array.isArray(ids) ? ids : []
  return (
    <>
      {list.map((id, index) => (
        <span key={id}>
          {index === 0 ? null : index === list.length - 1 ? ' and ' : ', '}
          <MemberLink to={memberProfilePath(id)} className="d2d-watch__leaderlink" testId="row-leader-link">
            <Person directory={directory} userId={id} />
          </MemberLink>
        </span>
      ))}
    </>
  )
}

/** The Group box — V2-8b, manager review round 2: the design's `r.groupVal`
 * / `r.groupWho`, beside the You box, with the avatar stack (above) and its
 * note moved inside it.
 *
 * A film has no episode position to report, so it reads the design's own
 * film branch instead — a plain count of who has seen it
 * (`membersWithStatus(..., 'watched')`, the same status `RowFaceStack`
 * already folds in above), never a position code.
 *
 * A series reads the group's own DERIVED position (`subscription.progress`
 * — the same field `SeasonTrack`'s track and `defaultSeasonNumber` already
 * read as "the group"), through `episodeCode` — never a second position
 * arithmetic. "Who is at the front" reuses `groupLeaderIds` (`watch.js`,
 * moved there in round 1 from `WatchTitle.jsx`, unchanged) — the SAME
 * function the title page's own group marker already calls (P6-283).
 *
 * V2-2c, manager review round 2: renders through `GroupLeaderNames` (this
 * file, immediately above) rather than `Person.jsx::People` directly —
 * each name is now this row's own entry point to that member's profile,
 * since `RowFaceStack`'s avatars no longer are one.
 */
function RowGroupBox({ subscription, directory, userId }) {
  const isFilm = subscription?.kind === 'movie'
  const progress = subscription?.progress
  const hasPosition = !isFilm && Number.isInteger(progress?.episode)
  const leaderIds = hasPosition ? groupLeaderIds(subscription) : []

  let groupVal
  if (isFilm) {
    const seen = membersWithStatus(subscription, 'watched').length
    groupVal = seen > 0 ? `${seen} have seen it` : 'Nobody yet'
  } else if (hasPosition) {
    groupVal = episodeCode(progress.season ?? null, progress.episode)
  } else {
    groupVal = 'No positions yet'
  }

  return (
    <div className="d2d-watch__groupbox" data-testid="row-group">
      <span className="d2d-micro d2d-watch__grouplabel">Group</span>
      <div className="d2d-watch__groupvalue">
        <span>{groupVal}</span>
        {leaderIds.length > 0 ? (
          <span className="d2d-watch__groupwho">
            (<GroupLeaderNames directory={directory} ids={leaderIds} />)
          </span>
        ) : null}
      </div>
      <RowFaceStack subscription={subscription} directory={directory} userId={userId} />
    </div>
  )
}

/** The You box — brief §2: "your position and the gap to the group, with
 * V2-8a's standing wording." `yourStanding` (`watch.js`) is the exact
 * function the title page's own standing box already calls; this renders
 * its `{value, note, tone}` in the design's ember-film box rather than a
 * second wording. `null` (nothing rendered) when signed out, matching
 * `yourStanding`'s own contract. */
function RowYouBox({ subscription, yourProgress, you, userId }) {
  const standing = yourStanding(subscription, yourProgress, you, userId)
  if (!standing) return null
  return (
    <div
      className={`d2d-watch__youbox d2d-watch__youbox--${standing.tone ?? 'plain'}`}
      data-testid="row-you"
    >
      <span className="d2d-micro d2d-watch__youlabel">You</span>
      <div className="d2d-watch__youvalue">{standing.value}</div>
      {standing.note ? <div className="d2d-micro d2d-watch__younote">{standing.note}</div> : null}
    </div>
  )
}

/** A live, ticking countdown — the ONLY thing on this panel that re-derives
 * off a clock faster than the page's own 60s tick (`PortalHome::useNow`).
 * Scoped to one row's next-up value, per Handoff's anime exact-grain state:
 * "ticks to the second." Runs its own 1s interval rather than borrowing the
 * page clock, so the cost is paid only by the rows that need it — an anime
 * subscription with a live `next_up`, which is the uncommon case.
 *
 * V2-8b, manager review round 2: RESTORED VERBATIM from the pre-redesign
 * `WatchPanel.jsx` (`origin/main`) rather than rewritten — the instruction
 * was explicit ("restore the removed countdown logic instead of rewriting
 * it"), and there is nothing about this component that the new single-line
 * layout needs to change; only where it is MOUNTED (`RowNext`, below) is
 * new. */
function TickingCountdown({ subscription }) {
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  const countdown = countdownFor(subscription, nowMs)
  if (!countdown) return null
  return (
    <span className="d2d-watch__countdown" data-testid="ticking-countdown">
      in {countdown.text}
    </span>
  )
}

/** `TickingCountdown`'s own sibling for `RowNextAwaiting` — the identical
 * per-second tick, off an explicit `airsAtMs` (`awaiting.next_air`) rather
 * than `subscription.next_up.airs_at`. A new component rather than a
 * parameter added to `TickingCountdown` itself: that component, and
 * `countdownFor` beneath it, are V2-8b's own countdown rules the brief says
 * must not change — `countdownForInstant` (`watch.js`) already factors out
 * the shared grain/formatting logic without touching either. */
function AwaitingTickingCountdown({ subscription, airsAtMs }) {
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  const countdown = countdownForInstant(subscription, airsAtMs, nowMs)
  if (!countdown) return null
  return (
    <span className="d2d-watch__countdown" data-testid="awaiting-ticking-countdown">
      in {countdown.text}
    </span>
  )
}

/**
 * V2-6b: the Awaiting shelf's own Next-line variant — brief §3. Mounted
 * ONLY when `isAwaitingNextSeason` is true (`RowNext`, above), so every
 * value here is read straight off `awaiting` — never a re-derivation of
 * WHICH case applies.
 *
 * - `awaiting.next_id` set → the sequel's own title (`awaitingBaseText`).
 * - `awaiting.season_number` set → `Season <N>`.
 * - Never aired at all → **"Not out yet"** (round 2 — see
 *   `awaitingBaseText`'s own docstring for why this is no longer folded
 *   into "Between seasons").
 * - Neither, and nothing has ever aired at all is false too → the design's
 *   own generic line, "Between seasons".
 *
 * "With `next_air`, the existing countdown's honesty rules apply" (brief
 * §3): the SAME grain (`isExactCountdown`), the SAME stale-suppression
 * (`isStale` — this title's own freshness signal, not specific to
 * `next_up`), and the SAME date format (`weekdayEasternDate`) as the
 * ordinary "Next" branch above, reused through `countdownForInstant` rather
 * than a second copy of the rules. No tonight-dot: that is specifically
 * about an EPISODE airing today (the this-week guide's own concept), and no
 * design state draws one for a season/sequel premiere.
 *
 * 🔴 Round 2: a PAST `next_air` (`_sequel_awaits` can hold `awaiting`
 * populated past AniList's own real premiere — see `isPastInstant`'s
 * docstring) draws NO countdown at all, on either grain —
 * `countdownForInstant` itself refuses it, and this component checks the
 * RESOLVED value rather than `airsAtMs` alone so no empty countdown box is
 * left behind either.
 *
 * Never invents "renewed" or "in production" (brief, verbatim) — only what
 * `awaiting`/`next_air` actually carry; undated reads "no date yet," never
 * a guessed date or season number.
 */
function RowNextAwaiting({ subscription, now }) {
  const base = awaitingBaseText(subscription)
  const airsAtMs = instantOf(subscription?.awaiting?.next_air ?? null)
  const stale = isStale(subscription, now)
  const exact = isExactCountdown(subscription)
  // Round 2 (manager ruling): resolved up front, rather than gating the
  // wrapper on `airsAtMs === null || stale` alone — `countdownForInstant`
  // itself now refuses a PAST instant (`isPastInstant`, `watch.js`), and
  // gating on that alone would still mount an empty `data-testid="countdown"`
  // box for one (`?.text` on a `null` countdown renders nothing, but the
  // surrounding div and its exact-grain child would still exist). Checking
  // the resolved value means NO countdown markup at all for a past instant,
  // on either grain — never a claim, however implicit, about time already
  // gone by.
  const countdown = airsAtMs === null || stale ? null : countdownForInstant(subscription, airsAtMs, now)
  return (
    <div className="d2d-watch__nextrow" data-testid="row-next">
      <span className="d2d-micro d2d-watch__label">Awaiting</span>
      <span className="d2d-watch__nextline">
        {base}
        {airsAtMs === null ? ' · no date yet' : ` · ${weekdayEasternDate(airsAtMs)}`}
      </span>
      {countdown === null ? null : (
        <div className="d2d-watch__count" data-testid="countdown">
          {exact ? (
            <AwaitingTickingCountdown subscription={subscription} airsAtMs={airsAtMs} />
          ) : (
            <span>{countdown.text}</span>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The design's "Next" line — `nextInfo()` in the prototype (`D2D Watch
 * v2.dc.html`, ~line 1247), read against this store's REAL status
 * vocabulary rather than the prototype's simplified fixture shape. Five
 * variants named there; this builds four of them plus an honest fallback,
 * reasoned out below rather than forcing all five onto data that cannot
 * support them (manager review round 2, item 1):
 *
 * - **Awaiting** — V2-6b: checked FIRST, unconditionally of `next_up`.
 *   `isAwaitingNextSeason` (`watch.js`) is the SAME predicate that put this
 *   row in the Awaiting tab in the first place — a row reached through that
 *   tab must always draw this variant, including the one sub-case where
 *   `next_up` is non-null (a never-aired title with a known premiere date:
 *   `_awaiting`'s "third shape" applies regardless of `next_up`, brief §2).
 *   See `RowNextAwaiting`, below.
 * - **Next** — a real `next_up`. The restored countdown logic
 *   (`countdownFor`/`isExactCountdown`/`isTonight`/`TickingCountdown`
 *   above) is reused UNCHANGED, only re-laid-out onto one line instead of
 *   the pre-redesign block's three. STALE (`isStale`) suppresses the
 *   countdown/tonight-dot exactly as the pre-redesign `NextUpBlock` did —
 *   label reads "Last known", no relative time — because `StaleStrip`
 *   below already gives the one reason once, and this line must not repeat
 *   a freshness claim `StaleStrip` has just disowned.
 * - **Film** — `kind === 'movie'`: `<runtime> · released <year>`
 *   (`movieRuntimeText`/`year`, `watch.js`), either half omitted when not
 *   on record — never a placeholder for a missing one.
 * - **Ended** — real `series_status === 'ended'`: "All N episodes are
 *   out," N from `episode_count`.
 * - **Logged** — NOT built. The design's own condition for it is "no
 *   `next`, not a film, no `seasons`" — on a WATCHLIST row (as opposed to
 *   the Everything archive V2-2b owns) that combination describes a title
 *   this bot has subscribed to but never refreshed (`series_status ===
 *   null`), which already has its own honest copy: "WAITING FOR THE FIRST
 *   CHECK." Reusing "Logged" there would tell a member their show has
 *   never aired, when the true fact is the bot has not checked yet — two
 *   different claims. So EVERY real state this component does not name
 *   above (never-checked, `cancelled`, or `between_seasons` with nothing
 *   `awaiting` names — which `isAwaitingNextSeason` already pulled onto its
 *   own tab, see above) falls through to the already-tested, honest
 *   `WhereThingsStandBlock` instead of forcing one of the design's five
 *   captions onto a state its own row enumeration never modelled — a
 *   deliberate, reasoned deviation from a literal five-variant reading, not
 *   an oversight.
 */
function RowNext({ subscription, now }) {
  if (isAwaitingNextSeason(subscription)) {
    return <RowNextAwaiting subscription={subscription} now={now} />
  }

  if (subscription?.next_up) {
    const nextUp = subscription.next_up
    const stale = isStale(subscription, now)
    const code = episodeCode(nextUp.season, nextUp.episode)
    const airsAtMs = instantOf(nextUp.airs_at)
    const exact = isExactCountdown(subscription)
    return (
      <div className="d2d-watch__nextrow" data-testid="row-next">
        <span className="d2d-micro d2d-watch__label">{stale ? 'Last known' : 'Next'}</span>
        <span className="d2d-watch__nextline">
          {code ? `${code} · ` : ''}
          {airsAtMs === null ? 'date unknown' : weekdayEasternDate(airsAtMs)}
        </span>
        {stale ? null : (
          <div className="d2d-watch__count" data-testid="countdown">
            {isTonight(subscription, now) ? (
              <span className="d2d-watch__tonightdot" aria-hidden="true" />
            ) : null}
            {exact ? (
              <TickingCountdown subscription={subscription} />
            ) : (
              <span>{countdownFor(subscription, now)?.text}</span>
            )}
          </div>
        )}
      </div>
    )
  }

  if (subscription?.kind === 'movie') {
    const runtimeText = movieRuntimeText(subscription?.runtime_minutes)
    const year = Number.isInteger(subscription?.year) ? subscription.year : null
    const parts = [runtimeText, year !== null ? `released ${year}` : null].filter(Boolean)
    return (
      <div className="d2d-watch__nextrow" data-testid="row-next">
        <span className="d2d-micro d2d-watch__label">Film</span>
        {parts.length > 0 ? <span className="d2d-watch__nextline">{parts.join(' · ')}</span> : null}
      </div>
    )
  }

  if (subscription?.series_status === 'ended') {
    const count = Number.isInteger(subscription?.episode_count) ? subscription.episode_count : null
    return (
      <div className="d2d-watch__nextrow" data-testid="row-next">
        <span className="d2d-micro d2d-watch__label">Ended</span>
        <span className="d2d-watch__nextline">
          {count !== null ? `All ${count} episodes are out` : 'The story has ended.'}
        </span>
      </div>
    )
  }

  return <WhereThingsStandBlock subscription={subscription} />
}

/** Handoff state 11's ember callout: `NOT CHECKED SINCE 17 SEP · 3 DAYS AGO`.
 *
 * V2-8b, manager review round 2: RESTORED, behaviour and gating unchanged
 * from the pre-redesign `WatchPanel.jsx` — `isStale` alone decides whether
 * this renders, independent of `next_up` (a released film or an ended show
 * can be stale too; the bot's nightly refresh checks providers and
 * release-date facts for those just as it checks a countdown for an airing
 * one). "Restyled to the design's tokens" turned out to need no CSS
 * changes at all: the pre-redesign rule already used the identical
 * ember-wash `rgba(255, 148, 85, 0.1)` this round's own `.d2d-watch__youbox`
 * independently converged on, plus the shared spacing/radius tokens — see
 * `app.css`'s own restored rule. */
function StaleStrip({ subscription, now }) {
  const checkedMs = instantOf(subscription?.last_checked)
  const days = checkedMs === null ? null : Math.floor((now - checkedMs) / (24 * 60 * 60 * 1000))
  return (
    <div className="d2d-watch__stalestrip" data-testid="stale-strip">
      <div className="d2d-micro">
        NOT CHECKED SINCE{' '}
        {checkedMs === null
          ? 'unknown'
          : `${shortEasternDate(checkedMs)} · ${days} day${days === 1 ? '' : 's'} ago`}
      </div>
      <p className="d2d-watch__staleline">The countdown is hidden because it may be out of date.</p>
    </div>
  )
}

/** "Where to watch" — Handoff view 1, item 6. Chips when the structured list
 * is populated; the subscribe-time sentence as a fallback otherwise (§4b of
 * the brief); the as-of line is always rendered when there is a date at all,
 * fresh or old, and never implied live.
 *
 * V2-8b, manager review round 2: RESTORED VERBATIM (Jordan's ruling: *"if
 * there is space on the watchlist row then they should appear there as
 * well"*) — same component, same honesty rules (`providers_as_of`, the
 * dated line, `providersOld`'s staleness), placed on the row after the Next
 * line. `.d2d-watch__provchips` already wraps (`flex-wrap: wrap`) and was
 * already proven safe at 320/375px on the title page's own hero before this
 * round — see the viewport run for the row-specific proof. No "+N" collapse
 * was built: wrapping already keeps the row from widening, which is the
 * one hard constraint ("they wrap within the row; they never widen it") —
 * see the report for the narrow-width measurement this decision rests on. */
function ProviderRow({ subscription, now }) {
  const providers = Array.isArray(subscription?.providers) ? subscription.providers : []
  const hasChips = providers.length > 0
  const label = typeof subscription?.providers_label === 'string' ? subscription.providers_label : null
  const asOfMs = instantOf(subscription?.providers_as_of)
  const old = providersOld(subscription, now)

  return (
    <div className="d2d-watch__provrow" data-testid="provider-row">
      <div className="d2d-micro d2d-watch__label">WHERE TO WATCH</div>
      {hasChips ? (
        <div className="d2d-watch__provchips">
          {providers.map((provider, index) => (
            <a
              // eslint-disable-next-line react/no-array-index-key
              key={`${provider.name}-${index}`}
              className="d2d-watch__provchip"
              href={subscription.providers_link ?? undefined}
              target="_blank"
              rel="noreferrer"
              aria-disabled={subscription.providers_link ? undefined : 'true'}
            >
              <ProviderLogo url={provider.logo_url} />
              <span>{provider.name}</span>
              <span className="d2d-micro">{provider.kind?.toUpperCase()}</span>
            </a>
          ))}
        </div>
      ) : (
        <p className="d2d-watch__noprov">{label ?? 'Not checked yet.'}</p>
      )}
      {hasChips ? (
        <p className="d2d-micro d2d-watch__provnote">
          Every link above opens the same regional guide.
        </p>
      ) : null}
      {asOfMs === null ? null : (
        <div className={`d2d-micro d2d-watch__note${old ? ' d2d-watch__note--old' : ''}`}>
          {old ? 'MAY HAVE MOVED · ' : ''}STREAMING LIST CHECKED {shortEasternDate(asOfMs)}
        </div>
      )}
    </div>
  )
}

/** One title's row — HANDOFF.md screen 1's "Row card." The hue wash is set
 * once, on the article's own root, through the identical `--d2d-title-hue`
 * custom property `WatchTitle.jsx`'s hero already uses (`hueForTitle`,
 * `watch.js`) — inherited by `.d2d-watch__poster--row`'s own glow rule
 * exactly the way the hero's poster picks it up, no prop threading through
 * `PosterSlot` needed.
 *
 * V2-8b, manager review round 2: the design's own element order — header,
 * [stale strip], Group/You boxes, season track, Next line, providers,
 * action bar. `now` is new (P6-244's own `useNow`, threaded down from
 * `WatchBody` exactly as `Guide` already receives it) — every restored
 * piece below needs a clock (`isStale`, the countdown, `providersOld`,
 * `StaleStrip`'s own day count). */
function Row({ subscription, directory, userId, onChanged, now, seasonCount = 1 }) {
  const you = deriveYou(subscription, userId)
  const yourProgress = deriveYourProgress(subscription, userId)
  const hue = hueForTitle(subscription)
  const openLabel = `Open ${typeof subscription?.title === 'string' ? subscription.title : 'this title'}`
  const stale = isStale(subscription, now)

  return (
    <article
      className="d2d-watch__row"
      data-testid="watch-row"
      data-kind={subscription.kind}
      style={{ '--d2d-title-hue': hue }}
    >
      <div className="d2d-watch__rowhead">
        <Link
          className="d2d-watch__rowposterlink"
          to={`/watch/${subscription.id}`}
          aria-label={openLabel}
        >
          <PosterSlot subscription={subscription} size="row" />
        </Link>
        <div className="d2d-watch__rowheadtext">
          <Link className="d2d-watch__rowtitlelink" to={`/watch/${subscription.id}`}>
            <h3 className="d2d-watch__title d2d-watch__title--row">{subscription.title}</h3>
          </Link>
          {
            // P6-298b §2.1: "N seasons" once the series behind this row has
            // more than one — never shown for a series of one, which reads
            // identically to a plain title (brief, verbatim).
          }
          {seasonCount > 1 ? (
            <span className="d2d-micro d2d-watch__seasoncount" data-testid="row-season-count">
              {seasonCount} seasons
            </span>
          ) : null}
          <RowMeta subscription={subscription} />
        </div>
      </div>

      {stale ? <StaleStrip subscription={subscription} now={now} /> : null}

      <div className="d2d-watch__standrow">
        <RowGroupBox subscription={subscription} directory={directory} userId={userId} />
        <RowYouBox subscription={subscription} yourProgress={yourProgress} you={you} userId={userId} />
      </div>
      <SeasonTrack subscription={subscription} yourProgress={yourProgress} />
      <RowNext subscription={subscription} now={now} />
      <ProviderRow subscription={subscription} now={now} />

      {
        // No `progressFallback` — V2-8a's own report, §1: "rows have no
        // copy-the-command control to fall back to; only the title page
        // does." `ActionBar` itself renders nothing at all when signed out.
      }
      <ActionBar
        subscription={subscription}
        yourProgress={yourProgress}
        userId={userId}
        you={you}
        onChanged={onChanged}
      />
    </article>
  )
}

/** One row of the week guide. */
function GuideRow({ day, isToday, entries }) {
  return (
    <div className="d2d-watch__guiderow" data-testid="guide-row">
      <span className={`d2d-watch__guideday d2d-micro${isToday ? ' d2d-watch__guideday--today' : ''}`}>
        {day.toUpperCase()}
      </span>
      <div className="d2d-watch__guideentries">
        {entries.length === 0 ? (
          <span className="d2d-micro d2d-watch__note">NOTHING</span>
        ) : (
          entries.map((entry) => (
            <div
              key={`${entry.subscriptionId}-${entry.season}-${entry.episode}`}
              className="d2d-watch__guideentry"
            >
              {entry.isToday ? <span className="d2d-watch__tonightdot" aria-hidden="true" /> : null}
              <span className="d2d-watch__guidetitle">
                {episodeCode(entry.season, entry.episode)} · {entry.title}
              </span>
              {entry.isStale ? (
                <span className="d2d-micro d2d-watch__note">
                  LAST KNOWN · NOT CHECKED SINCE{' '}
                  {shortEasternDate(entry.airsAtMs)}
                </span>
              ) : entry.isExact ? (
                <span className="d2d-micro">
                  {easternClock(entry.airsAtMs)} ET · EXACT · FROM ANILIST
                </span>
              ) : (
                <span className="d2d-micro d2d-watch__dashedbox">
                  NO AIR TIME PUBLISHED · CHECKED FROM 21:00 ET
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

/** The week guide — Handoff view 2. */
function Guide({ shared, now }) {
  const buckets = weekBuckets(shared, now)
  const empty = weekIsEmpty(buckets)
  const watchingCount = subscriptionsByStatus(shared, 'watching').length

  return (
    <section className="d2d-watch__guide" data-testid="watch-guide" aria-label="This week">
      <h3 className="d2d-watch__sectiontitle d2d-micro">What’s on this week</h3>
      {empty ? (
        <div data-testid="guide-empty">
          <p className="d2d-watch__flatline">Nothing airs between Sunday and Saturday.</p>
          <p className="d2d-watch__flatline">
            {watchingCount} title{watchingCount === 1 ? '' : 's'} on the list; none of them has an
            episode this week. Most weeks look like this.
          </p>
        </div>
      ) : (
        buckets.map((bucket) => (
          <GuideRow key={bucket.day} day={bucket.day} isToday={bucket.isToday} entries={bucket.entries} />
        ))
      )}
      <p className="d2d-micro d2d-watch__note">
        Exact times come from AniList; a dashed box means the date is real and the time is an
        assumption (21:00 ET).
      </p>
    </section>
  )
}

// V2-8b: the v1 compact `ArchiveRow`/`Archive` (Handoff view 3, the OLD
// always-visible "Finished" section stacked below Watching) are RETIRED —
// the v2 design's Finished TAB (below, `Tabs`/`WatchBody`) draws the same
// full `Row` card Watching does, per HANDOFF.md screen 1's own tabs-then-
// row-card structure, rather than a second, compact per-status presentation.

/** One dropped row — dashed empty poster, no date (see the finding on
 * `SERVER FINISHED`/`DROPPED` dates in `docs/reports/W2b.md`). */
function DroppedRow({ subscription }) {
  const code = episodeCode(subscription?.progress?.season ?? null, subscription?.progress?.episode)
  return (
    <div className="d2d-watch__droppedrow" data-testid="dropped-row">
      <span className="d2d-watch__poster d2d-watch__poster--dashed" aria-hidden="true" />
      <div>
        <div className="d2d-watch__title d2d-watch__title--row">{subscription.title}</div>
        <p className="d2d-micro d2d-watch__note">
          DROPPED{code ? ` · STOPPED AT ${code}` : ''}
        </p>
      </div>
    </div>
  )
}

/** Dropped — its own collapsed section, never the archive (Handoff view 4). */
function DroppedSection({ subscriptions, open, onToggle }) {
  if (subscriptions.length === 0) return null
  return (
    <section className="d2d-watch__dropped" data-testid="watch-dropped">
      <Disclosure open={open} onToggle={onToggle} data-testid="dropped-toggle">
        DROPPED · {subscriptions.length} · KEPT, NOT DELETED
      </Disclosure>
      {open ? (
        <div data-testid="dropped-list">
          <p className="d2d-watch__flatline">
            Dropping keeps the record. Nothing here is deleted.
          </p>
          {subscriptions.map((subscription) => (
            <DroppedRow key={subscription.id} subscription={subscription} />
          ))}
        </div>
      ) : null}
    </section>
  )
}

/** State 16 — the empty watchlist. A success, not an error: the first thing a
 * new member sees. */
function EmptyPanel() {
  return (
    <div className="d2d-watch__empty" data-testid="watch-empty">
      <h3 className="d2d-watch__emptytitle">Nothing on the list yet.</h3>
      <p className="d2d-watch__flatline">
        The server hasn’t subscribed to anything yet — add the first one from Discord.
      </p>
      <p className="d2d-watch__callout">
        <code>/watch add &lt;a show&gt;</code> → it lands here
      </p>
      <a className="d2d-watch__cta" href={DISCORD_HOME} target="_blank" rel="noreferrer">
        Open Discord ↗
      </a>
      <div className="d2d-watch__emptyposters" aria-hidden="true">
        <span className="d2d-watch__poster d2d-watch__poster--dashed" />
        <span className="d2d-watch__poster d2d-watch__poster--dashed" />
        <span className="d2d-watch__poster d2d-watch__poster--dashed" />
      </div>
    </div>
  )
}

/** V2-8b built "Tabs: Watching · Finished, each with a count" and left
 * Awaiting out for lack of data ("don't draw the tab until it has real
 * data; a tab that is always empty is a promise"). **V2-6b is that data**:
 * `watchListTab` (`watch.js`) now derives a genuine `awaiting` bucket from
 * published fields, so the third tab draws — `Watching · Awaiting next
 * season · Finished`, HANDOFF/RECONCILIATION's own order. Dropped keeps its
 * own section below the tabs, unconditional on which tab is active, per
 * RECONCILIATION §10 and the brief's explicit instruction — never a fourth
 * tab. */
const WATCH_TABS = [
  { key: 'watching', label: 'Watching' },
  { key: 'awaiting', label: 'Awaiting next season' },
  { key: 'finished', label: 'Finished' },
]

function Tabs({ shared, active, onSelect }) {
  return (
    <div className="d2d-watch__tabs" role="tablist" aria-label="Watchlist">
      {WATCH_TABS.map((tab) => {
        // P6-298b §2.1: "the tab counts count series" — the SAME function
        // that builds the tab's own row list (`WatchBody`, below), so the
        // count printed here and the number of rows shown can never disagree.
        const count = seriesRowsByListTab(shared, tab.key).length
        const isActive = tab.key === active
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`d2d-watch__tab${isActive ? ' d2d-watch__tab--active' : ''}`}
            data-testid={`watch-tab-${tab.key}`}
            onClick={() => onSelect(tab.key)}
          >
            {tab.label} · {count}
          </button>
        )
      })}
    </div>
  )
}

const TAB_EMPTY_TEXT = Object.freeze({
  watching: 'Nothing is being watched right now.',
  awaiting: 'Nothing is awaiting a next season right now.',
  finished: 'Nothing finished yet.',
})

/**
 * V2-4c: "Add a show" — `D2D Watch v2.dc.html`'s own `showAddBtn`/
 * `addShown`/`onAdd`/`addLabel` (brief §2, verbatim markup). `showAddBtn:
 * isLists && canAct` in the prototype becomes `signedIn` here (this
 * component is only ever mounted from the populated Watchlists screen, so
 * `isLists` is already true by construction); a signed-out visitor gets no
 * button at all, matching `AddPanel`'s own dependency on a session the
 * search Function requires (`docs/reports/V2-4a.md` §6 — a signed-out
 * request never reaches the platform service).
 *
 * The toggle button and the panel are additive to the existing "Open
 * Discord ↗" CTA above — brief §2's own "What must not change" (every
 * existing request, the rows, the tabs…) is read here as "do not remove
 * an existing affordance," not as "no new one may sit beside it."
 */
function AddShowSection({ signedIn }) {
  const [open, setOpen] = useState(false)
  if (!signedIn) return null

  return (
    <div className="d2d-watch__addsection">
      <button
        type="button"
        aria-expanded={open}
        className="d2d-watch__addtoggle"
        data-testid="add-show-toggle"
        onClick={() => setOpen((value) => !value)}
      >
        {open ? 'Close search' : 'Add a show'}
      </button>
      {open ? <AddPanel /> : null}
    </div>
  )
}

function WatchBody({ shared, directory, userId, now, droppedOpen, onToggleDropped, onChanged }) {
  const [tab, setTab] = useState('watching')
  // V2-6b: `subscriptionsByListTab` replaces the old direct
  // `subscriptionsByStatus(shared, 'watching'/'finished')` reads — for any
  // title `isAwaitingNextSeason` says no to, `watchListTab` returns exactly
  // that title's own `status`, so this is byte-for-byte the same set for
  // every title that ISN'T awaiting (the brief's own "must not change").
  // `subscriptionsByStatus(shared, 'dropped')` is untouched: Dropped is not
  // one of `watchListTab`'s three values, and stays its own section.
  //
  // P6-298b: `total` (the empty-panel gate, below) stays at the SUBSCRIPTION
  // level — "is there anything on the list at all" is a different question
  // from "how many ROWS show" once a series collapses several seasons onto
  // one. `seriesRowsByListTab` (§2.1's "one row per series") is what actually
  // feeds the tabs and their counts, immediately below.
  const watchingSubs = subscriptionsByListTab(shared, 'watching')
  const awaitingSubs = subscriptionsByListTab(shared, 'awaiting')
  const finishedSubs = subscriptionsByListTab(shared, 'finished')
  const dropped = subscriptionsByStatus(shared, 'dropped')
  const total = watchingSubs.length + awaitingSubs.length + finishedSubs.length + dropped.length

  if (total === 0) return <EmptyPanel />

  const watching = seriesRowsByListTab(shared, 'watching')
  const awaiting = seriesRowsByListTab(shared, 'awaiting')
  const finished = seriesRowsByListTab(shared, 'finished')
  const buckets = { watching, awaiting, finished }
  const active = buckets[tab] ?? watching

  return (
    <>
      {
        // Handoff's accent table: "Panel, populated -> the one ember fill:
        // Add a title in Discord (header)." That is a property of the SCREEN
        // (is the list populated at all), not of `watching` specifically — a
        // watchlist that is all finished/dropped and nothing currently
        // watching is still a populated panel and still needs its one ember
        // control, or the screen would render none at all.
      }
      <a className="d2d-watch__cta" href={DISCORD_HOME} target="_blank" rel="noreferrer">
        Open Discord ↗
      </a>
      <AddShowSection signedIn={typeof userId === 'string' && userId !== ''} />
      <Guide shared={shared} now={now} />
      <Tabs shared={shared} active={tab} onSelect={setTab} />
      <div className="d2d-watch__cards" data-testid="watch-cards">
        {active.length === 0 ? (
          <p className="d2d-watch__flatline">{TAB_EMPTY_TEXT[tab]}</p>
        ) : (
          active.map(({ subscription, seasonCount }) => (
            <Row
              key={subscription.id}
              subscription={subscription}
              directory={directory}
              userId={userId}
              onChanged={onChanged}
              now={now}
              seasonCount={seasonCount}
            />
          ))
        )}
      </div>
      <DroppedSection subscriptions={dropped} open={droppedOpen} onToggle={onToggleDropped} />
    </>
  )
}

export default function WatchPanel({ directory, now, userId, docPath }) {
  const watch = useWatch()
  const [droppedOpen, setDroppedOpen] = useState(false)

  const shared = watch.state === READY ? watch.shared : null
  const stamp = freshness(shared?.as_of, now)

  return (
    <section className="d2d-portal__panel d2d-watch" aria-labelledby="portal-watch" data-testid="watch-panel">
      <div className="d2d-portal__panelhead">
        <h2 id="portal-watch" className="d2d-portal__paneltitle d2d-micro">
          Watch
        </h2>
        {watch.state === LOADING ? null : (
          <span
            className="d2d-portal__stamp d2d-micro"
            title={stamp.title ?? undefined}
            data-testid="watch-as-of"
          >
            {watch.state === READY ? stamp.label : 'Unavailable'}
          </span>
        )}
      </div>

      {
        // A later coordination note: the watch screen tabs (Watchlists ·
        // Archive — only the two screens that exist; Leaderboards/stats and
        // Goated arrive with V2-5/V2-8c) are ONE shared component,
        // `WatchScreenTabs.jsx`, rendered here in the panel's own header
        // area AND at the top of `WatchArchive.jsx` — never a second,
        // independent tab row. This replaces the plain "ARCHIVE →" link
        // this panel opened with; the tab row states the identical
        // destination with more context (and the sibling "Watchlists" tab,
        // which this panel IS, reading as the active one).
      }
      <WatchScreenTabs active="lists" />

      {watch.state === LOADING ? (
        <p className="d2d-portal__flat" data-testid="watch-loading">
          Loading the watchlist…
        </p>
      ) : watch.state === MISCONFIGURED ? (
        <p className="d2d-portal__flat" data-testid="watch-misconfigured">
          Watch together is set up wrong on this deployment, so it cannot be shown until that is
          fixed — this is not about your account. Everything else on this page is unaffected.
        </p>
      ) : watch.state !== READY ? (
        <p className="d2d-portal__flat" data-testid="watch-unavailable">
          Watch together is unavailable right now. Everything else on this page is unaffected.
        </p>
      ) : (
        <WatchBody
          shared={shared}
          directory={directory}
          now={now}
          userId={userId}
          droppedOpen={droppedOpen}
          onToggleDropped={() => setDroppedOpen((open) => !open)}
          onChanged={watch.confirm}
        />
      )}

      {
        // 🔴 P6-316. THE DOCS LINK, AT THE FOOT, AS `MusicPanel` DRAWS ITS OWN. The
        // branch has carried `docSection: 'docs-watch'` since P6-298g and this panel
        // took no `docPath`, so the field was right and drew nothing. Neither watch
        // design (v1 HANDOFF.md, v2 HANDOFF.md / RECONCILIATION.md, or any of the
        // prototypes) draws a docs link anywhere, so there is no placement to
        // follow — the foot is the portal's convention. Drawn in every state, the
        // unavailable ones included: a docs page does not depend on `/api/watch`.
        //
        // THE WORDS FOLLOW THE PAGE (the review's ruling). `/docs/watch` opens by
        // calling the feature the crew's shared list, and *How watch works →* read
        // as a verb phrase. `doc-links.test.jsx` pins them exactly.
      }
      {docPath ? (
        <Link className="d2d-portal__more d2d-micro" to={docPath}>
          How the watch list works →
        </Link>
      ) : null}
    </section>
  )
}
